import React, { useEffect, useMemo, useState } from 'react';
import {
  Add,
  ChevronLeft,
  ExpandMore,
  Language,
  Logout,
  MenuOutlined,
  NotificationsNone,
  OpenInNew,
  Person,
  Search,
  SettingsOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLayout } from '../context/LayoutContext';
import { useRouteHistory } from '../context/RouteHistoryContext';
import { useSettings } from '../context/SettingsContext';
import { useNotification } from '../context/NotificationContext';
import { logout as logoutService } from '../services/authService';
import { getMyProfile, updateMyProfile } from '../services/userServices';
import { formatDateTime } from '../utils/dateFormatter';
import ConfirmDialog from './ui/ConfirmDialog';
import { CrmButton, CrmInput, CrmModal } from './ui';
import '../assets/styles/Topbar.scss';

const SEARCHABLE_PAGES = [
  { label: 'Dashboard', path: '/dashboard', module: 'dashboard' },
  { label: 'Contacts', path: '/leads', module: 'leads' },
  { label: 'Customers', path: '/customers', module: 'customers' },
  { label: 'Quotations', path: '/quotations', module: 'quotations' },
  { label: 'Orders', path: '/orders', module: 'orders' },
  { label: 'Invoices', path: '/invoices', module: 'invoices' },
  { label: 'Proforma Invoices', path: '/proforma-invoices', module: 'invoices' },
  { label: 'Products', path: '/products/list', module: 'products' },
  { label: 'Categories', path: '/categories', module: 'products' },
  { label: 'Attributes', path: '/attributes', module: 'products' },
  { label: 'KOT Board', path: '/kots', module: 'kots', cateringOnly: true },
  { label: 'Deliveries', path: '/deliveries', module: 'deliveries' },
  { label: 'Payments', path: '/payments', module: 'payments' },
  { label: 'Payment History', path: '/payments/history', module: 'payments' },
  { label: 'Storefronts', path: '/storefronts', module: 'website', operationalModule: 'website' },
  { label: 'Media', path: '/media', module: 'dashboard' },
  { label: 'Reports', path: '/reports', module: 'reports' },
  { label: 'Notifications', path: '/notifications', module: 'dashboard' },
  { label: 'Settings', path: '/settings', module: 'settings' },
  { label: 'Users', path: '/users', module: 'users' },
];

const QUICK_CREATE = [
  { label: 'Create contact', path: '/leads/new', module: 'leads' },
  { label: 'Create quotation', path: '/quotation-create', module: 'quotations' },
  { label: 'Create invoice', path: '/invoices/create', module: 'invoices' },
  { label: 'Create proforma invoice', path: '/proforma-invoices/create', module: 'invoices' },
  { label: 'Add product', path: '/products/new', module: 'products' },
];

const isActiveRecord = (record) => record?.is_active == null || ![false, 0, '0', 'false'].includes(record.is_active);
const ensureExternalUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

function Topbar() {
  const navigate = useNavigate();
  const { canGoBack, goBack } = useRouteHistory();
  const { toggleSidebar } = useLayout();
  const { currentUser, logout, updateCurrentUser, canAccessModule } = useAuth();
  const { settings, commercialCatalog, operationalAvailability, isOperationalModuleAvailable } = useSettings();
  const {
    unreadNotifications,
    totalUnseen,
    fetchNotifications,
    fetchBubbleCounts,
    markNotificationSeen,
    markAllNotificationsSeen,
  } = useNotification();

  const [search, setSearch] = useState('');
  const [createAnchor, setCreateAnchor] = useState(null);
  const [storefrontAnchor, setStorefrontAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone_number: '' });

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return SEARCHABLE_PAGES
      .filter((item) => canAccessModule(item.module))
      .filter((item) => !item.operationalModule || isOperationalModuleAvailable(item.operationalModule))
      .filter((item) => !item.cateringOnly || String(settings?.business_type || '').toUpperCase() === 'CATERING')
      .filter((item) => item.label.toLowerCase().includes(term))
      .slice(0, 7);
  }, [search, canAccessModule, isOperationalModuleAvailable, settings?.business_type]);

  const quickCreate = useMemo(
    () => QUICK_CREATE.filter((item) => canAccessModule(item.module)),
    [canAccessModule]
  );

  const activeStorefronts = useMemo(
    () => (operationalAvailability?.activeStorefronts || commercialCatalog?.storefronts || []).filter(isActiveRecord),
    [operationalAvailability?.activeStorefronts, commercialCatalog?.storefronts]
  );

  const latestUnreadNotifications = useMemo(() => [...unreadNotifications]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [unreadNotifications]);

  const go = (path) => {
    setSearch('');
    setCreateAnchor(null);
    setStorefrontAnchor(null);
    setAccountAnchor(null);
    navigate(path);
  };

  const getNotificationFallbackRoute = (notification) => {
    if (!notification) return '/dashboard';
    if (notification.module === 'leads' && notification.source_id) return `/leads/${notification.source_id}/edit`;
    if (notification.module === 'work_orders' && notification.source_id) return `/workorders/${notification.source_id}`;
    if (notification.module === 'orders' && notification.source_id) return `/orders/${notification.source_id}`;
    if (notification.module === 'kot') return '/kots';
    if (notification.module === 'delivery') return '/deliveries';
    if (notification.module === 'feedback') return '/feedbacks';
    return '/dashboard';
  };

  const formatModuleLabel = (module) => {
    const key = String(module || '').trim().toLowerCase();
    if (key === 'leads') return 'Contacts';
    if (key === 'work_orders') return 'Work Orders';
    if (key === 'kot') return 'KOT';
    if (key === 'delivery') return 'Delivery';
    if (key === 'feedback') return 'Feedback';
    return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const toTitleCase = (value) => String(value || '')
    .split('_').join(' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const getNotificationActionDetails = (action) => {
    const raw = String(action || '').trim();
    if (!raw) return { message: 'Update', statusChange: '' };
    const transitionMatch = raw.match(/\(([^)]+)->([^)]+)\)/);
    const statusChange = transitionMatch
      ? `${toTitleCase(transitionMatch[1].trim())} -> ${toTitleCase(transitionMatch[2].trim())}`
      : '';
    return {
      message: raw.replace(/\(([^)]+)->([^)]+)\)/g, '').replace(/\s+/g, ' ').trim() || 'Update',
      statusChange,
    };
  };

  const appendQueryParam = (path, key, value) => {
    const safeValue = Number(value || 0);
    if (!path || !safeValue) return path;
    return `${path}${path.includes('?') ? '&' : '?'}${key}=${safeValue}`;
  };

  const appendTextQueryParam = (path, key, value) => {
    const safeValue = String(value || '').trim();
    if (!path || !safeValue) return path;
    return `${path}${path.includes('?') ? '&' : '?'}${key}=${encodeURIComponent(safeValue)}`;
  };

  const extractWorkOrderNumber = (notification) => {
    const match = String(notification?.action || '').match(/WO\/\d{4}\/\d+/i);
    return match ? match[0].toUpperCase() : '';
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;
    if (!notification.isSeen) await markNotificationSeen(notification.id);
    setNotificationAnchor(null);

    let finalRoute = notification.module === 'leads' && notification.source_id
      ? `/leads/${notification.source_id}/edit`
      : (notification.redirect_url || getNotificationFallbackRoute(notification));

    if (notification.module === 'kot' && notification.source_id) {
      finalRoute = appendQueryParam('/kots', 'focusKotId', notification.source_id);
      finalRoute = appendTextQueryParam(finalRoute, 'focusWoNo', extractWorkOrderNumber(notification));
      finalRoute = appendTextQueryParam(finalRoute, 'range', 'all');
    }
    if (notification.module === 'delivery' && notification.source_id) {
      finalRoute = appendQueryParam('/deliveries', 'focusDeliveryId', notification.source_id);
      finalRoute = appendTextQueryParam(finalRoute, 'range', 'all');
    }
    if (notification.module === 'feedback' && notification.source_id) {
      finalRoute = appendQueryParam('/feedbacks', 'focusFeedbackId', notification.source_id);
    }

    navigate(finalRoute);
    await fetchBubbleCounts();
  };

  const openNotifications = async (event) => {
    setNotificationAnchor(event.currentTarget);
    await fetchNotifications();
    await fetchBubbleCounts();
  };

  const handleOpenProfile = async () => {
    setAccountAnchor(null);
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileError('');
    try {
      const profile = await getMyProfile();
      setProfileForm({
        name: profile?.name || currentUser?.name || '',
        email: profile?.email || currentUser?.email || '',
        phone_number: profile?.phone_number || '',
      });
    } catch (error) {
      setProfileError(error?.response?.data?.error || 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileError('');
    try {
      const result = await updateMyProfile(profileForm);
      if (result?.user) {
        updateCurrentUser({
          name: result.user.name,
          email: result.user.email,
          phone_number: result.user.phone_number || null,
        });
      }
      setProfileOpen(false);
    } catch (error) {
      setProfileError(error?.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = async () => {
    try { await logoutService(); } catch (_) {}
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (!profileOpen) setProfileError('');
  }, [profileOpen]);

  return (
    <>
      <header className="hub-topbar">
        <button className="hub-topbar__brand" type="button" onClick={() => go('/dashboard')}>
          <img src="/jdh-logo.png" alt="" />
          <span>JDH CRM</span>
        </button>

        <Tooltip title="Navigation">
          <IconButton className="hub-topbar__nav-toggle" onClick={toggleSidebar} aria-label="Toggle navigation"><MenuOutlined /></IconButton>
        </Tooltip>

        {canGoBack ? (
          <Tooltip title="Back">
            <IconButton className="hub-topbar__back" onClick={goBack} aria-label="Go back"><ChevronLeft /></IconButton>
          </Tooltip>
        ) : null}

        <div className="hub-topbar__search-wrap">
          <Search />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Find or Ask"
            aria-label="Search the workspace"
          />
          {search ? (
            <div className="hub-topbar__results">
              {results.length ? results.map((item) => (
                <button key={item.path} type="button" onMouseDown={() => go(item.path)}>{item.label}</button>
              )) : <span className="hub-topbar__no-results">No matching page</span>}
            </div>
          ) : null}
        </div>

        <Tooltip title="Quick add">
          <IconButton className="hub-topbar__create" onClick={(event) => setCreateAnchor(event.currentTarget)} aria-label="Quick add">
            <Add />
          </IconButton>
        </Tooltip>

        <div className="hub-topbar__spacer" />
        <div className="hub-topbar__tools">
          {canAccessModule('website') && activeStorefronts.length ? (
            <Tooltip title="Storefronts">
              <IconButton onClick={(event) => setStorefrontAnchor(event.currentTarget)} aria-label="Storefront links"><StorefrontOutlined /></IconButton>
            </Tooltip>
          ) : null}

          <Tooltip title="Notifications">
            <IconButton onClick={openNotifications} aria-label="Open notifications">
              <Badge badgeContent={totalUnseen} color="error"><NotificationsNone /></Badge>
            </IconButton>
          </Tooltip>

          {canAccessModule('settings') ? (
            <Tooltip title="Settings">
              <IconButton onClick={() => go('/settings')} aria-label="Settings"><SettingsOutlined /></IconButton>
            </Tooltip>
          ) : null}
        </div>

        <Divider orientation="vertical" flexItem className="hub-topbar__divider" />
        <button className="hub-topbar__account" type="button" onClick={(event) => setAccountAnchor(event.currentTarget)}>
          <Avatar>{String(currentUser?.name || currentUser?.email || 'U').charAt(0).toUpperCase()}</Avatar>
          <span>{currentUser?.name || currentUser?.email || 'My account'}</span>
          <ExpandMore />
        </button>
      </header>

      <Menu anchorEl={createAnchor} open={Boolean(createAnchor)} onClose={() => setCreateAnchor(null)} slotProps={{ paper: { className: 'hub-topbar__menu' } }}>
        {quickCreate.map((item) => <MenuItem key={item.path} onClick={() => go(item.path)}>{item.label}</MenuItem>)}
      </Menu>

      <Menu anchorEl={storefrontAnchor} open={Boolean(storefrontAnchor)} onClose={() => setStorefrontAnchor(null)} slotProps={{ paper: { className: 'hub-topbar__menu hub-topbar__storefront-menu' } }}>
        <div className="hub-topbar__menu-heading">Storefronts</div>
        {activeStorefronts.map((storefront) => {
          const key = storefront.code || storefront.id;
          const externalUrl = ensureExternalUrl(storefront.domain);
          return (
            <div className="hub-topbar__storefront-row" key={storefront.id || key}>
              <MenuItem onClick={() => go(`/storefronts/${encodeURIComponent(key)}/experience`)}>
                <Language fontSize="small" />
                <ListItemText primary={storefront.name || storefront.code || 'Storefront'} secondary={storefront.domain || 'Manage experience'} />
              </MenuItem>
              {externalUrl ? (
                <Tooltip title="Open storefront">
                  <IconButton component="a" href={externalUrl} target="_blank" rel="noreferrer" aria-label={`Open ${storefront.name || 'storefront'}`}>
                    <OpenInNew />
                  </IconButton>
                </Tooltip>
              ) : null}
            </div>
          );
        })}
        <Divider />
        <MenuItem onClick={() => go('/storefronts')}>Manage storefronts</MenuItem>
      </Menu>

      <Menu anchorEl={notificationAnchor} open={Boolean(notificationAnchor)} onClose={() => setNotificationAnchor(null)} slotProps={{ paper: { className: 'hub-topbar__notifications' } }}>
        {unreadNotifications.length ? (
          <div className="hub-topbar__notification-head">
            <Typography variant="subtitle2">Notifications</Typography>
            <Button
              size="small"
              onClick={async () => {
                await markAllNotificationsSeen();
                await fetchBubbleCounts();
                setNotificationAnchor(null);
              }}
            >
              Mark all as read
            </Button>
          </div>
        ) : null}
        {latestUnreadNotifications.length ? latestUnreadNotifications.slice(0, 20).map((notification) => {
          const actionDetails = getNotificationActionDetails(notification.action);
          const feedbackBadge = String(notification?.module || '').toLowerCase() === 'feedback'
            ? (/(create|new|added)/.test(String(notification?.action || '').toLowerCase()) ? 'NEW' : 'UPDATED')
            : '';
          return (
            <MenuItem key={notification.id} onClick={() => handleNotificationClick(notification)} className="hub-topbar__notification-item">
              <div>
                <Typography variant="body2" className="hub-topbar__notification-message">{actionDetails.message}</Typography>
                <div className="hub-topbar__notification-meta">
                  <span>
                    {actionDetails.statusChange ? <small>{actionDetails.statusChange}</small> : null}
                    <b>{formatModuleLabel(notification.module)}</b>
                    {feedbackBadge ? <Chip label={feedbackBadge} size="small" color={feedbackBadge === 'NEW' ? 'error' : 'warning'} /> : null}
                  </span>
                  <small>{formatDateTime(notification.created_at || new Date())}</small>
                </div>
              </div>
            </MenuItem>
          );
        }) : <MenuItem disabled>No unread notifications</MenuItem>}
        <Divider />
        <MenuItem onClick={() => { setNotificationAnchor(null); navigate('/notifications'); }}>
          <ListItemText primary="View all notifications" secondary="Search, filter and review notification history" />
        </MenuItem>
      </Menu>

      <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)} slotProps={{ paper: { className: 'hub-topbar__menu' } }}>
        <MenuItem onClick={handleOpenProfile}>
          <Person fontSize="small" />
          <ListItemText primary="My account" secondary="Profile details" />
        </MenuItem>
        {canAccessModule('settings') ? <MenuItem onClick={() => go('/settings')}><SettingsOutlined fontSize="small" /><ListItemText primary="Settings" /></MenuItem> : null}
        <Divider />
        <MenuItem onClick={() => { setAccountAnchor(null); setLogoutDialogOpen(true); }}><Logout fontSize="small" /><ListItemText primary="Sign out" /></MenuItem>
      </Menu>

      <CrmModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        title="Edit Profile"
        size="md"
        footer={(
          <>
            <CrmButton variant="secondary" onClick={() => setProfileOpen(false)} disabled={savingProfile}>Cancel</CrmButton>
            <CrmButton variant="primary" onClick={handleSaveProfile} loading={savingProfile} disabled={profileLoading}>Save</CrmButton>
          </>
        )}
      >
        <div className="topbar-profile-form">
          {profileError ? <p className="topbar-profile-error" role="alert">{profileError}</p> : null}
          <CrmInput label="Name" value={profileForm.name} onChange={(event) => setProfileForm((prev) => ({ ...prev, name: event.target.value }))} disabled={profileLoading || savingProfile} />
          <CrmInput label="Email" type="email" value={profileForm.email} onChange={(event) => setProfileForm((prev) => ({ ...prev, email: event.target.value }))} disabled={profileLoading || savingProfile} />
          <CrmInput label="Phone Number" type="tel" value={profileForm.phone_number} onChange={(event) => setProfileForm((prev) => ({ ...prev, phone_number: event.target.value }))} disabled={profileLoading || savingProfile} />
        </div>
      </CrmModal>

      <ConfirmDialog
        open={logoutDialogOpen}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={() => { setLogoutDialogOpen(false); handleLogout(); }}
        onCancel={() => setLogoutDialogOpen(false)}
      />
    </>
  );
}

export default Topbar;
